import { Box } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { DepartmentType } from "../../../../utils/models";
import { type LocationEditorDesign, regularFieldSx } from "./consts";

type FacilityTypeFieldCommonProps = {
	departmentType: DepartmentType | undefined;
	onDepartmentTypeChange: (departmentType: DepartmentType) => void;
	onDropdownOpen: () => void;
	onDropdownClose: () => void;
};

/** v2 alone has a read-only state: the Locations page's editor under a Schedule Template edit lock. */
export type FacilityTypeFieldProps = FacilityTypeFieldCommonProps &
	({ design: Extract<LocationEditorDesign, "v1"> } | { design: Extract<LocationEditorDesign, "v2">; disabled: boolean });

/** A Synergy facility's type. The type names are shown as-is, as `DepartmentTypeChips` shows them. */
export const FacilityTypeField: FC<FacilityTypeFieldProps> = (props): ReactElement => {
	const { departmentType, onDepartmentTypeChange, onDropdownOpen, onDropdownClose } = props;
	const { t } = useTranslation();
	// The type names are proper nouns, shown as-is in every language.
	const items = [
		{ value: DepartmentType.SNF, label: "SNF" },
		{ value: DepartmentType.ALF, label: "ALF" },
		{ value: DepartmentType.Hospital, label: "Hospital" },
	];
	const handleChange = (item: DepartmentType | null | undefined): void => {
		if (item) {
			onDepartmentTypeChange(item);
		}
	};
	switch (props.design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						width: "100%",
						alignItems: "start",
					}}
				>
					<SystemDropdown
						onOpen={onDropdownOpen}
						onClose={onDropdownClose}
						label={t("schedule.template.v3.editor.facilityType")}
						selectSx={regularFieldSx}
						items={items}
						selectedItem={departmentType}
						onChange={handleChange}
					/>
				</Box>
			);
		case "v2":
			return (
				<SystemDropdown
					onOpen={onDropdownOpen}
					onClose={onDropdownClose}
					label={t("schedule.template.v3.editor.facilityType")}
					items={items}
					selectedItem={departmentType}
					onChange={handleChange}
					fullWidth
					disabled={props.disabled}
					data-testid="location-facility-type-dropdown"
				/>
			);
	}
};
